import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Forward,
  Loader2,
  Reply,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  patientHasIdentity,
  useCareCommunicationThread,
  useExportCareCommunication,
  type CareCommunicationListItem,
  type ThreadSegment,
} from '@/hooks/useCareCommunications';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { AckBadge, StatusBadge } from './CareCommunications';

export function CareCommunicationThread() {
  const { communicationIdentifier: rawId } = useParams<{
    communicationIdentifier: string;
  }>();
  const communicationIdentifier = rawId ? decodeURIComponent(rawId) : undefined;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const { data, isLoading, error } = useCareCommunicationThread(
    activeClinicId,
    communicationIdentifier,
  );
  const exportMutation = useExportCareCommunication();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data || !communicationIdentifier) {
    return (
      <div className="min-h-screen bg-page-light p-6 dark:bg-page-dark">
        <ErrorAlert message={t('communicate.error_not_found')} />
      </div>
    );
  }

  const assembled = data.assembled;
  const segments = assembled?.segments ?? [];
  const hasCpr = patientHasIdentity(assembled?.patient);
  const patientName = [assembled?.patient?.given?.join(' '), assembled?.patient?.family]
    .filter(Boolean)
    .join(' ')
    .trim();

  async function handleExport(messageId: string, format: 'json' | 'xml') {
    if (!activeClinicId) return;
    try {
      await exportMutation.mutateAsync({ clinicId: activeClinicId, messageId, format });
      toast.success(t('communicate.toast_exported'));
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('communicate.errors.export_failed'),
      );
    }
  }

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-4xl px-6">
          <button
            type="button"
            onClick={() => navigate('/care-communications')}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('communicate.back_to_list')}
          </button>

          <div className="md:flex md:items-start md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {assembled?.topic?.trim() ||
                  data.thread?.subject?.trim() ||
                  t('communicate.thread_title')}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {assembled?.categoryCode
                  ? t(`communicate.category_${assembled.categoryCode}`, {
                      defaultValue: assembled.categoryCode,
                    })
                  : t('communicate.thread_subtitle')}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {hasCpr ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    CPR
                  </span>
                ) : null}
                {patientName ? (
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {patientName}
                  </span>
                ) : null}
                {data.thread?.status ? (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t(`communicate.thread_status_${data.thread.status}`, {
                      defaultValue: data.thread.status,
                    })}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 md:ml-4 md:mt-0">
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    `/care-communications/${encodeURIComponent(communicationIdentifier)}/reply`,
                  )
                }
              >
                <Reply className="mr-1 h-4 w-4" />
                {t('communicate.reply')}
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    `/care-communications/${encodeURIComponent(communicationIdentifier)}/forward`,
                  )
                }
              >
                <Forward className="mr-1 h-4 w-4" />
                {t('communicate.forward')}
              </Button>
            </div>
          </div>

          {/* Ordered assembled segments */}
          <section className="mt-8">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('communicate.section_segments')}
            </h3>
            {segments.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                {t('communicate.no_segments')}
              </p>
            ) : (
              <ul className="mt-4 space-y-4">
                {segments.map((seg) => (
                  <SegmentCard key={seg.segmentId} segment={seg} />
                ))}
              </ul>
            )}
          </section>

          {/* Message list with ack + export */}
          <section className="mt-10">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('communicate.section_messages')}
            </h3>
            {data.messages.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                {t('communicate.no_messages')}
              </p>
            ) : (
              <ul className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-card-dark">
                {data.messages.map((msg) => (
                  <MessageRow
                    key={msg.id}
                    item={msg}
                    exporting={exportMutation.isPending}
                    onExportJson={() => void handleExport(msg.id, 'json')}
                    onExportXml={() => void handleExport(msg.id, 'xml')}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function SegmentCard({ segment }: { segment: ThreadSegment }) {
  const { t } = useTranslation();
  return (
    <li className="rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-card-dark">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span>
          {new Date(segment.timestamp).toLocaleString('da-DK')}
          {segment.activityCode
            ? ` · ${t(`communicate.activity_${segment.activityCode}`, {
                defaultValue: segment.activityCode,
              })}`
            : ''}
        </span>
        <span>
          {segment.sender.organizationName || segment.sender.sorId || '—'}
          {segment.sender.practitionerName
            ? ` · ${segment.sender.practitionerName}`
            : ''}
        </span>
      </div>
      {segment.kind === 'string' ? (
        <p className="mt-2 whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
          {segment.text}
        </p>
      ) : (
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          {t('communicate.attachment_label')}:{' '}
          {segment.attachment?.title || segment.attachment?.contentType || '—'}
        </p>
      )}
    </li>
  );
}

function MessageRow({
  item,
  exporting,
  onExportJson,
  onExportXml,
}: {
  item: CareCommunicationListItem;
  exporting: boolean;
  onExportJson: () => void;
  onExportXml: () => void;
}) {
  const { t } = useTranslation();
  const dateIso =
    item.direction === 'in'
      ? item.receivedAt ?? item.createdAt
      : item.sentAt ?? item.createdAt;

  return (
    <li className="px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                item.direction === 'in'
                  ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
                  : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
              }`}
            >
              {t(`communicate.direction_${item.direction}`)}
            </span>
            <StatusBadge status={item.status} />
            <AckBadge item={item} />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t('communicate.col_sender')}: {item.senderSor ?? '—'} →{' '}
            {t('communicate.col_receiver')}: {item.receiverSor ?? '—'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {dateIso ? new Date(dateIso).toLocaleString('da-DK') : '—'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={onExportJson}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            {t('communicate.export_json')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={onExportXml}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            {t('communicate.export_xml')}
          </Button>
        </div>
      </div>
    </li>
  );
}
